import type { Dispatch } from '@reduxjs/toolkit';
import type { MutableRefObject } from 'react';

import { applyActionResults } from '../state/aiSidekickChatSlice';
import type { StreamActionResult } from './actionResult';
import { toMessageActionResult } from './messageActionResult';
import type { StreamEvent } from './streamEvent';

/** Action results one turn has received, held until the turn ends. */
export type StreamActionTurn = {
  results: StreamActionResult[];
  /** The turn's message id, from the first result that named it. */
  messageId?: string;
  /** The answer this turn finalized, which its results belong on. */
  answerId?: string;
};

/**
 * Starts an empty action turn.
 * @returns A turn holding no action results.
 */
export const createActionTurn = (): StreamActionTurn => ({ results: [] });

/**
 * Holds an `actionResult` frame on the turn.
 * @param event - The decoded stream frame.
 * @param turn - The turn's held action results.
 * @returns Whether the frame was an action result.
 */
export const bufferActionEvent = (
  event: StreamEvent,
  turn: StreamActionTurn
): boolean => {
  if ('actionResult' in event) {
    turn.results.push(event.actionResult);
    turn.messageId ??= event.messageId;
    return true;
  }
  return false;
};

/**
 * Puts the turn's action results on its answer, then empties the turn. Runs
 * when the turn ends, since a result can land before or after the answer's
 * `messageDone`.
 * @param turnRef - The turn's held action results.
 * @param dispatch - The chat store's dispatch.
 */
export const flushActionTurn = (
  turnRef: MutableRefObject<StreamActionTurn>,
  dispatch: Dispatch
): void => {
  const { results, messageId, answerId } = turnRef.current;
  turnRef.current = createActionTurn();
  if (results.length === 0) {
    return;
  }
  const turnKey = `action-result-${messageId ?? Date.now()}`;
  dispatch(
    applyActionResults({
      actionResults: results.map((result, index) =>
        toMessageActionResult(`${turnKey}-${index}`, result)
      ),
      answerId,
      fallbackMessageId: turnKey,
      createdAt: new Date().toISOString(),
    })
  );
};
